// Mirrors pbp-api's insights date-range.ts, in UTC, so a preset applied to a from/to filter
// pair selects the same window the API would resolve for that preset.
type Window = { from: string; to: string }

const DAY_MS = 24 * 60 * 60 * 1000

const startOfDay = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
const endOfDay = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999))
const startOfYear = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
const endOfYear = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), 11, 31, 23, 59, 59, 999))
const addDays = (d: Date, days: number) => new Date(d.getTime() + days * DAY_MS)
const subYears = (d: Date, years: number) =>
  new Date(Date.UTC(d.getUTCFullYear() - years, d.getUTCMonth(), d.getUTCDate()))

const lastDays = (now: Date, days: number) => ({ from: startOfDay(addDays(now, 1 - days)), to: endOfDay(now) })

const RESOLVERS: Record<string, (now: Date) => { from: Date; to: Date }> = {
  last7days: now => lastDays(now, 7),
  last30days: now => lastDays(now, 30),
  last90days: now => lastDays(now, 90),
  last12months: now => ({ from: startOfDay(addDays(subYears(now, 1), 1)), to: endOfDay(now) }),
  ytd: now => ({ from: startOfYear(now), to: endOfDay(now) }),
  lastyear: now => ({ from: startOfYear(subYears(now, 1)), to: endOfYear(subYears(now, 1)) })
}

export const resolveDatePreset = (key: string, now: Date = new Date()): Window | undefined => {
  const resolve = RESOLVERS[key]

  if (!resolve) return undefined

  const { from, to } = resolve(now)

  return { from: from.toISOString(), to: to.toISOString() }
}

export const matchDatePreset = (keys: string[], window: Partial<Window>, now: Date = new Date()): string | undefined =>
  keys.find(key => {
    const resolved = resolveDatePreset(key, now)

    return resolved?.from === window.from && resolved?.to === window.to
  })
